<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import MmDisclosure from '../base/MmDisclosure.vue'

const props = withDefaults(defineProps<{
  sections: Array<{ heading: string; lines: string[] }>
  activeLine: number
  fullText?: boolean
  seekable?: boolean
  takeover?: boolean
  estimated?: boolean
}>(), { fullText: false, seekable: false, takeover: false })
const emit = defineEmits<{ seek: [line: number] }>()
const { t } = useI18n()
const scroller = ref<HTMLElement | null>(null)
let followAfter = 0
let followTimer: ReturnType<typeof setTimeout> | undefined
const pauseFollow = () => {
  followAfter = Date.now() + 5000
  clearTimeout(followTimer)
  followTimer = setTimeout(() => { void follow() }, 5000)
}
onBeforeUnmount(() => clearTimeout(followTimer))
const indexedSections = computed(() => {
  let index = 0
  return props.sections.map(section => ({
    heading: section.heading,
    lines: section.lines.map(text => ({ text, index: index++ })),
  }))
})
const follow = async () => {
  if (props.fullText || props.activeLine < 0 || Date.now() < followAfter) return
  await nextTick()
  const box = scroller.value
  const line = box?.querySelector<HTMLElement>(`[data-line="${props.activeLine}"]`)
  if (!box || !line) return
  const top = box.scrollTop + line.getBoundingClientRect().top - box.getBoundingClientRect().top - box.clientHeight * .32
  box.scrollTo({ top: Math.max(0, top), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' })
}
watch([() => props.activeLine, () => props.fullText, () => props.takeover], follow, { immediate: true })
</script>

<template>
  <div ref="scroller" class="lyr" :class="{ static: fullText, 'lyr-takeover': takeover, estimated }" :aria-label="t('music.lyrics')" tabindex="0" @wheel.passive="pauseFollow" @touchmove.passive="pauseFollow" @pointerdown="pauseFollow" @keydown="pauseFollow">
    <template v-for="(section, index) in indexedSections" :key="index">
      <MmDisclosure v-if="section.heading.length > 80" class="lyrics-notes" variant="divider" nested :label="t('mm.song.linerNotes')" :summary="section.heading">
        <p>{{ section.heading }}</p>
      </MmDisclosure>
      <h3 v-else-if="section.heading" class="sec">{{ section.heading }}</h3>
      <template v-for="line in section.lines" :key="line.index">
        <p v-if="fullText" class="ln">{{ line.text }}</p>
        <button v-else type="button" class="ln" :class="{ distant: estimated && activeLine >= 0 && Math.abs(line.index - activeLine) > 1, now: line.index === activeLine, adjacent: activeLine >= 0 && Math.abs(line.index - activeLine) === 1, past: activeLine >= 0 && line.index < activeLine }"
          :data-line="line.index" :aria-current="line.index === activeLine ? 'true' : undefined" :disabled="!seekable"
          :aria-label="t('mm.song.seekLine', { line: line.text })" @click="emit('seek', line.index)">{{ line.text }}</button>
      </template>
    </template>
  </div>
</template>

<style scoped>
  .lyr { position: relative; max-height: 560px; overflow: auto; padding: 12px 0 200px; scroll-behavior: smooth; mask-image: linear-gradient(transparent, var(--text) 8%, var(--text) 72%, transparent); scrollbar-width: none; }
  .lyr::-webkit-scrollbar { display: none; }
  .lyr .sec { font: 500 11px/1 var(--font-mono); letter-spacing: .14em; text-transform: uppercase; color: var(--text-3); margin: 22px 0 10px; }
  .lyr .ln { display: block; width: 100%; text-align: left; font: 700 clamp(22px, 2.6vw, 32px)/1.28 var(--font-ui); letter-spacing: -.02em; color: var(--text); opacity: .32; padding: 4px 0; border-radius: var(--s-2); transition: opacity var(--d-slow) var(--ease-out), transform var(--d-slow) var(--ease-out), color var(--d-slow); transform-origin: left center; }
  .lyr .ln:hover { opacity: .6; }
  .lyr .ln.past { opacity: .5; }
  .lyr .ln.now { opacity: 1; color: var(--live); transform: scale(1.02); }
  .lyr.static .ln { font-size: 18px; font-weight: 500; opacity: .9; line-height: 1.6; padding: 0; }
  .lyr.static { max-height: none; mask-image: none; padding-bottom: 0; }


.lyr { overscroll-behavior: contain; }
.sec { overflow-wrap: anywhere; }
.ln { margin: 0; overflow-wrap: anywhere; position: relative; }
.ln::after { content: ''; position: absolute; inset: min(0px, calc((100% - 44px) / 2)) 0; }
.ln:focus-visible { opacity: 1; }
.lyrics-notes { margin-block: 16px; }
.lyr-takeover { min-height: 0; max-height: none; height: 100%; padding: 20vh 0 40vh; }
.lyr-takeover .ln { color: var(--takeover-ink); font-size: clamp(30px, 4.6vw, 64px); max-width: 18ch; }
.lyr-takeover .ln.now { color: var(--takeover-ink); }
.lyr-takeover .sec { color: color-mix(in srgb, var(--takeover-ink) 55%, transparent); }
/* Keep T21a's estimated timing behavior when exact backend timestamps are absent. */
.estimated .ln.distant { opacity: 0; }
.estimated .ln.distant:focus-visible { opacity: 1; }
.estimated .ln.adjacent { opacity: .65; }
.estimated .ln.now { transform: none; }
@media(prefers-reduced-motion:reduce) { .lyr { scroll-behavior: auto; } .ln.now { transform: none; } }

</style>
